import type { FC, ReactNode, RefObject } from 'react'
import type { ProjectSummary } from '@/types'

import { X } from 'lucide-react'
import { groupDigits, pad2 } from '@/lib/format'
import { TextLink } from '@/components/generals/text-link'

import { OFFICES } from '@/constants/navigation'

interface AtlasFactsProps {
    project: ProjectSummary
    index: number
    titleId: string
    titleRef: RefObject<HTMLHeadingElement | null>
    onClose: () => void
    extra?: ReactNode
}

const formatCoordinate = (value: number, positive: string, negative: string): string => `${Math.abs(value).toFixed(2)}° ${value >= 0 ? positive : negative}`

/**
 * The facts panel the Atlas opens for one project.
 */
export const AtlasFacts: FC<AtlasFactsProps> = ({ project, index, titleId, titleRef, onClose, extra }): ReactNode => {
    const office = OFFICES.find(entry => entry.code === project.leadOffice)

    const rows: Array<{ label: string, value: ReactNode }> = [
        { label: 'Year', value: project.year },
        {
            label: 'Area',
            value: (
                <>
                    <span>
                        {groupDigits(project.areaM2)}
                    </span>
                    {' '}m²
                </>
            )
        },
        {
            label: 'Type',
            value: (
                <span data-mdlfy-cms={`projects:${project.id}:building_type`}>
                    {project.buildingType}
                </span>
            )
        },
        {
            label: 'Structure',
            value: (
                <span data-mdlfy-cms={`projects:${project.id}:structural_system`}>
                    {project.structuralSystem}
                </span>
            )
        },
        {
            label: 'Status',
            value: (
                <span data-mdlfy-cms={`projects:${project.id}:status`}>
                    {project.status}
                </span>
            )
        },
        {
            label: 'Energy',
            value: (
                <>
                    <span>
                        {project.energyKwhM2}
                    </span>
                    {' '}kWh/m²
                </>
            )
        },
        {
            label: 'Embodied carbon',
            value: (
                <>
                    <span>
                        {project.carbonKgM2}
                    </span>
                    {' '}kgCO2e/m²
                </>
            )
        }
    ]

    if (project.lat !== null && project.lon !== null) {
        rows.push({ label: 'Position', value: `${formatCoordinate(project.lat, 'N', 'S')} / ${formatCoordinate(project.lon, 'E', 'W')}` })
    }

    return (
        <>
            <div className='flex items-center justify-between gap-4 border-b border-hairline py-3 pl-gutter pr-3 desk:pl-6'>
                <p className='hud text-mute'>
                    Project {pad2(index + 1)} / Led from {office?.city ?? project.leadOffice}
                </p>

                <button type='button' onClick={onClose} className='grid size-10 place-items-center text-ink hover:text-signal'>
                    <X className='size-4' strokeWidth={1.5} aria-hidden='true' />

                    <span className='sr-only'>
                        Close project facts
                    </span>
                </button>
            </div>

            <div className='flex-1 overflow-y-auto px-gutter py-6 desk:px-6'>
                {extra}

                <h3
                    id={titleId}
                    ref={titleRef}
                    tabIndex={-1}
                    className='display-md outline-none'
                    data-mdlfy-cms={`projects:${project.id}:name`}
                >
                    {project.name}
                </h3>

                <p className='hud mt-3 text-mute' data-mdlfy-cms={`projects:${project.id}:city`}>
                    {project.city}
                </p>

                <p className='mt-6 text-[15px] leading-[1.6] text-ink/85' data-mdlfy-cms={`projects:${project.id}:summary`}>
                    {project.summary}
                </p>

                <dl className='mt-8 border-t border-hairline'>
                    {rows.map(row => (
                        <div key={row.label} className='flex justify-between gap-6 border-b border-hairline py-3'>
                            <dt className='hud text-mute'>
                                {row.label}
                            </dt>

                            <dd className='hud text-right text-ink'>
                                {row.value}
                            </dd>
                        </div>
                    ))}
                </dl>

                <TextLink href={`/projects/${project.slug}`} className='mt-8'>
                    Open project
                </TextLink>
            </div>
        </>
    )
}
